import {
  Nullable,
  Nullish,
  request,
  ResponseBody,
} from "@tager/admin-services";
import { OutgoingValueUnion } from "@tager/admin-dynamic-field";

import {
  BrandType,
  CategoryType,
  CollectionType,
  FieldOptionType,
  FieldType,
  FullBrandType,
  FullCategoryType,
  FullCollectionType,
  FullFieldType,
  FullSpecialType,
  FullProductType,
  SpecialType,
  ProductType,
  ProductTypeType,
  TermLinkType,
  TermType,
  ProductGroupType,
  PriceMultipleValue,
  CategorySeoType,
  BasisProductType,
  FullBasisProductType,
  BasisProductParam,
  ProductSearchType,
  SearchType,
  FullProductTypeType,
} from "@/typings/model";

export type CountData = { count: number };
export type DirectionType = "up" | "down";
type SuccessData = { success: boolean };

/** Product */

export function getProductCount(): Promise<ResponseBody<CountData>> {
  return request.get({ path: "/admin/products/count" });
}

export function getProductList(params?: {
  query?: string;
  pageNumber?: number;
  pageSize?: number;
}): Promise<ResponseBody<Array<ProductType>>> {
  return request.get({ path: "/admin/products", params });
}

export function cloneProduct(id: number | string): Promise<{ data: any }> {
  return request.post({ path: `/admin/products/${id}/clone` });
}

export function getProductSearchList(params?: {
  query?: string;
}): Promise<ResponseBody<Array<ProductSearchType>>> {
  return request.get({ path: "/admin/products/search", params });
}

export function getProduct(
  productId: string | number
): Promise<ResponseBody<FullProductType>> {
  return request.get({ path: `/admin/products/${productId}` });
}

export function deleteProduct(
  productId: string | number
): Promise<SuccessData> {
  return request.delete({ path: `/admin/products/${productId}` });
}

export function setPriorityProduct(
  productId: string | number,
  payload: { value: number }
): Promise<SuccessData> {
  return request.put({
    path: `/admin/products/${productId}/priority`,
    body: payload,
  });
}

export type ProductCreationPayload = {
  productType: Nullable<number>;
  category: Nullable<number>;
  brand: Nullable<number>;
  title: string;
  subtitle: string;
};

export function createProduct(
  payload: ProductCreationPayload
): Promise<ResponseBody<FullProductType>> {
  return request.post({ path: "/admin/products", body: payload });
}

export type ProductUpdatePayload = {
  category: Nullable<number>;
  brand: Nullable<number>;
  title: string;
  subtitle: string;

  content: Nullable<string>;
  contentPrice: Nullable<string>;
  image: Nullable<string>;
  urlAlias: string;

  gallery: Array<{ id: Nullish<string>; description: Nullish<string> }>;
  schemaImage: Nullable<string>;
  youtubeVideo: Nullable<string>;
  video: Nullable<string>;

  fieldValues: Array<{
    fieldId: number;
    value: OutgoingValueUnion | Array<OutgoingValueUnion>;
  }>;
  sameProducts: Array<number>;

  collection: Nullable<number>;
  productGroups: Array<number>;
  specialOffers: Array<number>;

  price: number;
  discountType: Nullable<string>;
  discountValue: Nullable<number>;
  priceMultipleEnabled: boolean;
  priceMultipleLabel: string;
  priceMultipleValues: Array<PriceMultipleValue>;
  availability: Nullable<string>;
  waitingDays: Nullable<number>;
  availableQuantity: Nullable<number>;

  pageDescription: string;
  openGraphImage: Nullable<string>;
  pageTitle: string;
};

export function updateProduct(
  productId: number | string,
  payload: ProductUpdatePayload
): Promise<ResponseBody<FullProductType>> {
  return request.put({
    path: `/admin/products/${productId}`,
    body: payload,
  });
}

/** Basis Product */

export function getBasisProductList(params?: {
  query?: string;
  pageNumber?: number;
  pageSize?: number;
}): Promise<ResponseBody<Array<BasisProductType>>> {
  return request.get({ path: "/admin/basis-products", params });
}

export function getBasisProduct(
  productId: string | number
): Promise<ResponseBody<FullBasisProductType>> {
  return request.get({ path: `/admin/basis-products/${productId}` });
}

export function deleteBasisProduct(
  productId: string | number
): Promise<SuccessData> {
  return request.delete({ path: `/admin/basis-products/${productId}` });
}

export type BasisProductCreationPayload = {
  name: string;
  params: Array<BasisProductParam>;
  products: Array<{
    options: Array<number>;
    product: Nullable<number>;
  }>;
};

export function createBasisProduct(
  payload: BasisProductCreationPayload
): Promise<ResponseBody<FullBasisProductType>> {
  return request.post({ path: "/admin/basis-products", body: payload });
}

export type BasisProductUpdatePayload = BasisProductCreationPayload;

export function updateBasisProduct(
  productId: number | string,
  payload: BasisProductUpdatePayload
): Promise<ResponseBody<FullBasisProductType>> {
  return request.put({
    path: `/admin/basis-products/${productId}`,
    body: payload,
  });
}

/** Category */

export function getCategoryCount(): Promise<ResponseBody<CountData>> {
  return request.get({ path: "/admin/categories/count" });
}

export function getCategory(
  categoryId: string | number
): Promise<ResponseBody<FullCategoryType>> {
  return request.get({ path: `/admin/categories/${categoryId}` });
}

export function getCategoryList(params?: {
  query?: string;
  pageSize?: number;
  pageNumber?: number;
}): Promise<ResponseBody<Array<CategoryType>>> {
  return request.get({ path: "/admin/categories", params });
}

export function switchCategoryType(
  categoryId: number
): Promise<ResponseBody<FullCategoryType>> {
  return request.post({ path: `/admin/categories/${categoryId}/switch-type` });
}

export function deleteCategory(
  categoryId: string | number
): Promise<SuccessData> {
  return request.delete({ path: `/admin/categories/${categoryId}` });
}

export function moveCategory(
  categoryId: string | number,
  direction: string
): Promise<SuccessData> {
  return request.post({
    path: `/admin/categories/${categoryId}/move/${direction}`,
  });
}

export type CategoryCreationPayload = {
  name: string;
  parent: Nullable<number>;
  type: string;
};

export function createCategory(
  payload: CategoryCreationPayload
): Promise<ResponseBody<FullCategoryType>> {
  return request.post({ path: "/admin/categories", body: payload });
}

export type ConditionUpdateType = {
  readonly type: Nullable<string>;
  readonly productTypeId: Nullable<number>;
  readonly fieldConditions: Array<{
    readonly fieldId: Nullable<number>;
    readonly operator: Nullable<string>;
    readonly operand: Nullable<number | boolean>;
    readonly optionIds: Array<number>;
  }>;
  readonly brandIds: Array<number>;
  readonly operator: Nullable<string>;
  readonly operand: Nullable<number>;
};

export type CategoryUpdatePayload = {
  urlPath: string;
  content: string;
  image: Nullable<string>;
  related: Array<{ id: number; label: string }>;
  pageDescription: string;
  openGraphImage: Nullable<string>;
  pageTitle: string;
  conditions: Array<ConditionUpdateType>;
  filters: Array<{
    label: string;
    fields: Array<number>;
    description: string;
    coefficient: string;
  }>;
  cross: Nullable<
    Array<{
      name: string;
      items: Array<{ name: string; categoryId: Nullable<number> }>;
    }>
  >;
} & CategoryCreationPayload;

export function updateCategory(
  categoryId: number | string,
  payload: CategoryUpdatePayload
): Promise<ResponseBody<FullCategoryType>> {
  return request.put({
    path: `/admin/categories/${categoryId}`,
    body: payload,
  });
}

/** Seo Category */

export function getSeoList(params?: {
  query?: string;
  pageNumber?: number;
  pageSize?: number;
}): Promise<ResponseBody<Array<CategorySeoType>>> {
  return request.get({ path: "/admin/categories/seo", params });
}

export function updateSeoForCategory(
  categoryId: number,
  payload: {
    title: string;
    description: string;
  }
): Promise<ResponseBody<CategorySeoType>> {
  return request.put({
    path: `/admin/categories/${categoryId}/seo`,
    body: payload,
  });
}

/** Field */

export function getFieldList(
  productType?: string | number,
  params?: {
    query?: string;
  }
): Promise<ResponseBody<Array<FieldType>>> {
  return request.get({
    path: "/admin/fields",
    params: { productType: productType ?? "", ...params },
  });
}

export function getField(
  fieldId: string | number
): Promise<ResponseBody<FullFieldType>> {
  return request.get({ path: `/admin/fields/${fieldId}` });
}

export function deleteField(fieldId: string | number): Promise<SuccessData> {
  return request.delete({ path: `/admin/fields/${fieldId}` });
}

export function moveField(
  fieldId: string | number,
  direction: string
): Promise<SuccessData> {
  return request.post({
    path: `/admin/fields/${fieldId}/move/${direction}`,
  });
}

export type FieldCreationPayload = {
  name: string;
  systemName: string;
  productTypeId: string | number;
  type: string;
};

export function createField(
  payload: FieldCreationPayload
): Promise<ResponseBody<FullFieldType>> {
  return request.post({ path: "/admin/fields", body: payload });
}

export type FieldUpdatePayload = {
  name: string;
  systemName: string;
  type: string;
  adminNote: string;
  description: Nullable<string>;
  multiple: Nullable<boolean>;
  noLabel: Nullable<string>;
  yesLabel: Nullable<string>;
  options: Nullable<Array<FieldOptionType>>;
  colors: Nullable<Array<FieldOptionType>>;
};

export function updateField(
  brandId: string | number,
  payload: FieldUpdatePayload
): Promise<ResponseBody<FullFieldType>> {
  return request.put({ path: `/admin/fields/${brandId}`, body: payload });
}

/** Brand */

export function getBrandCount(): Promise<ResponseBody<CountData>> {
  return request.get({ path: "/admin/brands/count" });
}

export function getBrandList(params?: {
  query?: string;
  pageNumber?: number;
  pageSize?: number;
}): Promise<ResponseBody<Array<BrandType>>> {
  return request.get({ path: "/admin/brands", params });
}

export function deleteBrand(brandId: string | number): Promise<SuccessData> {
  return request.delete({ path: `/admin/brands/${brandId}` });
}

export function getBrand(
  brandId: string | number
): Promise<ResponseBody<FullBrandType>> {
  return request.get({ path: `/admin/brands/${brandId}` });
}

export type BrandCreationPayload = {
  name: string;
};

export function createBrand(
  payload: BrandCreationPayload
): Promise<ResponseBody<FullBrandType>> {
  return request.post({ path: "/admin/brands", body: payload });
}

export type BrandUpdatePayload = BrandCreationPayload & {
  urlAlias: string;
  image: Nullable<string>;
  description: string;
  content: string;
  pageDescription: string;
  openGraphImage: Nullable<string>;
  pageTitle: string;
};

export function updateBrand(
  brandId: string | number,
  payload: BrandUpdatePayload
): Promise<ResponseBody<FullBrandType>> {
  return request.put({ path: `/admin/brands/${brandId}`, body: payload });
}

/** Collection */

export function getCollectionsCount(): Promise<ResponseBody<CountData>> {
  return request.get({ path: "/admin/collections/count" });
}

export function getCollectionList(params?: {
  query?: string;
  pageNumber?: number;
  pageSize?: number;
}): Promise<ResponseBody<Array<CollectionType>>> {
  return request.get({ path: "/admin/collections", params });
}

export function getCollectionSearchList(params?: {
  query?: string;
  brand?: number | string;
}): Promise<ResponseBody<Array<SearchType>>> {
  return request.get({ path: "/admin/collections/search", params });
}

export function deleteCollection(
  collectionId: string | number
): Promise<SuccessData> {
  return request.delete({ path: `/admin/collections/${collectionId}` });
}

export function getCollection(
  collectionId: string | number
): Promise<ResponseBody<FullCollectionType>> {
  return request.get({ path: `/admin/collections/${collectionId}` });
}

export type CollectionCreationPayload = {
  name: string;
  brandId: Nullable<number>;
  content: string;
  image: Nullable<string>;
  pageDescription: string;
  openGraphImage: Nullable<string>;
  pageTitle: string;
};

export function createCollection(
  payload: CollectionCreationPayload
): Promise<ResponseBody<FullCollectionType>> {
  return request.post({ path: "/admin/collections", body: payload });
}

export type CollectionUpdatePayload = {
  urlAlias: string;
} & CollectionCreationPayload;

export function updateCollection(
  collectionId: string | number,
  payload: CollectionUpdatePayload
): Promise<ResponseBody<FullCollectionType>> {
  return request.put({
    path: `/admin/collections/${collectionId}`,
    body: payload,
  });
}

/** Special */

export function getSpecialsCount(): Promise<ResponseBody<CountData>> {
  return request.get({ path: "/admin/special-offers/count" });
}

export function getSpecialList(params?: {
  query?: string;
  pageNumber?: number;
  pageSize?: number;
}): Promise<ResponseBody<Array<SpecialType>>> {
  return request.get({ path: "/admin/special-offers", params });
}

export function deleteSpecial(
  specialId: string | number
): Promise<SuccessData> {
  return request.delete({ path: `/admin/special-offers/${specialId}` });
}

export function getSpecial(
  specialId: string | number
): Promise<ResponseBody<FullSpecialType>> {
  return request.get({ path: `/admin/special-offers/${specialId}` });
}

export type SpecialCreationPayload = {
  name: string;
  content: string;
  image: Nullable<string>;
  pageDescription: string;
  openGraphImage: Nullable<string>;
  pageTitle: string;
};

export function createSpecial(
  payload: SpecialCreationPayload
): Promise<ResponseBody<FullSpecialType>> {
  return request.post({ path: "/admin/special-offers", body: payload });
}

export type SpecialUpdatePayload = {
  urlAlias: string;
} & SpecialCreationPayload;

export function updateSpecial(
  specialId: string | number,
  payload: SpecialUpdatePayload
): Promise<ResponseBody<FullSpecialType>> {
  return request.put({
    path: `/admin/special-offers/${specialId}`,
    body: payload,
  });
}

/** Product Type */

export function getProductTypeCount(): Promise<ResponseBody<CountData>> {
  return request.get({ path: "/admin/product-types/count" });
}

export function getProductTypeList(params?: {
  query?: string;
  pageNumber?: number;
  pageSize?: number;
}): Promise<ResponseBody<Array<ProductTypeType>>> {
  return request.get({ path: "/admin/product-types", params });
}

export function deleteProductType(
  productTypeId: string | number
): Promise<SuccessData> {
  return request.delete({
    path: `/admin/product-types/${productTypeId}`,
  });
}

export function getProductType(
  productTypeId?: number | string
): Promise<ResponseBody<FullProductTypeType>> {
  return request.get({ path: `/admin/product-types/${productTypeId}` });
}

export type ProductTypeCreationPayload = {
  name: string;
  systemName: string;
};

export function createProductType(
  payload: ProductTypeCreationPayload
): Promise<ResponseBody<ProductTypeType>> {
  return request.post({
    path: `/admin/product-types`,
    body: payload,
  });
}

export type ProductTypeUpdatePayload = {
  defaultCategoryId: Nullable<number>;
} & ProductTypeCreationPayload;

export function updateProductType(
  productTypeId: string | number,
  payload: ProductTypeUpdatePayload
): Promise<ResponseBody<ProductTypeType>> {
  return request.put({
    path: `/admin/product-types/${productTypeId}`,
    body: payload,
  });
}

/** Product group */

export function getProductGroupList(params?: {
  query?: string;
  pageNumber?: number;
  pageSize?: number;
}): Promise<ResponseBody<Array<ProductGroupType>>> {
  return request.get({ path: "/admin/product-groups", params });
}

export function deleteProductGroup(
  productGroupId: string | number
): Promise<SuccessData> {
  return request.delete({
    path: `/admin/product-groups/${productGroupId}`,
  });
}

export function getProductGroup(
  productGroupId: number | string
): Promise<ResponseBody<ProductGroupType>> {
  return request.get({ path: `/admin/product-groups/${productGroupId}` });
}

export type ProductGroupCreationPayload = {
  name: string;
  systemName: string;
};

export function createProductGroup(
  payload: ProductGroupCreationPayload
): Promise<ResponseBody<ProductGroupType>> {
  return request.post({
    path: `/admin/product-groups`,
    body: payload,
  });
}

export type ProductGroupUpdatePayload = ProductGroupCreationPayload;

export function updateProductGroup(
  productGroupId: string | number,
  payload: ProductGroupUpdatePayload
): Promise<ResponseBody<ProductGroupType>> {
  return request.put({
    path: `/admin/product-groups/${productGroupId}`,
    body: payload,
  });
}

/** Term */

export type TermCreationPayload = {
  term: string;
  value: string;
};

export type TermUpdatePayload = TermCreationPayload;

export function getTermList(): Promise<ResponseBody<Array<TermType>>> {
  return request.get({ path: "/admin/vocabulary" });
}

export function getTermCount(): Promise<ResponseBody<CountData>> {
  return request.get({ path: "/admin/vocabulary/count" });
}

export function getTerm(
  termId: string | number
): Promise<ResponseBody<TermType>> {
  return request.get({ path: `/admin/vocabulary/${termId}` });
}

export function createTerm(
  payload: TermCreationPayload
): Promise<ResponseBody<TermType>> {
  return request.post({ path: "/admin/vocabulary", body: payload });
}

export function updateTerm(
  termId: string | number,
  payload: TermUpdatePayload
): Promise<ResponseBody<TermType>> {
  return request.put({
    path: `/admin/vocabulary/${termId}`,
    body: payload,
  });
}

export function deleteTerm(termId: string | number): Promise<SuccessData> {
  return request.delete({
    path: `/admin/vocabulary/${termId}`,
  });
}

/** TermLink */

export type TermLinkCreationPayload = {
  label: string;
  url: string;
};

export type TermLinkUpdatePayload = TermLinkCreationPayload;

export function getTermLinkList(
  termId: number | string
): Promise<ResponseBody<Array<TermLinkType>>> {
  return request.get({ path: `/admin/vocabulary/${termId}/links` });
}

export function getTermLink(
  termId: number | string
): Promise<ResponseBody<TermLinkType>> {
  return request.get({ path: `/admin/vocabulary/links/${termId}` });
}

export function createTermLink(
  termId: number | string,
  payload: TermLinkCreationPayload
): Promise<ResponseBody<TermLinkType>> {
  return request.post({
    path: `/admin/vocabulary/${termId}/links`,
    body: payload,
  });
}

export function updateTermLink(
  termLinkId: string | number,
  payload: TermLinkUpdatePayload
): Promise<ResponseBody<TermLinkType>> {
  return request.put({
    path: `/admin/vocabulary/links/${termLinkId}`,
    body: payload,
  });
}

export function deleteTermLink(
  termLinkId: string | number
): Promise<SuccessData> {
  return request.delete({
    path: `/admin/vocabulary/links/${termLinkId}`,
  });
}

export function moveTermLink(
  termLinkId: string | number,
  direction: DirectionType
): Promise<SuccessData> {
  return request.post({
    path: `/admin/vocabulary/links/${termLinkId}/move/${direction}`,
  });
}
